import { useFrame, useLoader } from '@react-three/fiber'
import { useMapImageCache } from 'features/kestrelLite/hooks/useMapImageCache'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { DoubleSide, RepeatWrapping, TextureLoader } from 'three'

type MapStyle = 'street' | 'satellite'

/** Bounding extent of the tiles in world units */
export interface BoundingExtent {
  width: number
  depth: number
  centerX: number
  centerZ: number
}

interface MapGroundPlaneProps {
  lat: number
  long: number
  metersPerUnit?: number
  yPosition?: number
  scale?: number
  rotationY?: number
  xOffset?: number
  zOffset?: number
  mapStyle?: MapStyle
  boundingExtent?: BoundingExtent
  onLoad?: () => void
  onLoadingChange?: (loading: boolean) => void
  onStyleFailed?: (style: MapStyle) => void
  /** Kestrel model ID for caching map images to rolodex */
  kestrelModelId?: string
}

interface MapLayerProps extends MapGroundPlaneProps {
  targetOpacity: number
  onFadeComplete?: () => void
  /** Override the map URL to use (e.g., from cache) */
  overrideMapUrl?: string
}

/**
 * Single map layer with fade animation support
 */
const MapLayer: FC<MapLayerProps> = ({
  lat,
  long,
  metersPerUnit = 1,
  yPosition = -30,
  rotationY = 180,
  mapStyle = 'street',
  boundingExtent,
  targetOpacity,
  onFadeComplete,
  onLoad,
  overrideMapUrl,
}) => {
  const mapService =
    mapStyle === 'satellite' ? 'World_Imagery' : 'World_Street_Map'
  const baseOpacity = mapStyle === 'satellite' ? 0.7 : 1

  // Calculate degrees per meter at this latitude
  const metersPerDegreeLat = 111320
  const metersPerDegreeLong = 111320 * Math.cos((lat * Math.PI) / 180)

  // Calculate map extent based on bounding box or use defaults
  const mapExtent = useMemo(() => {
    if (boundingExtent) {
      // Add 20% padding around the bounding extent
      const padding = 1.2
      const widthMeters = boundingExtent.width * metersPerUnit * padding
      const depthMeters = boundingExtent.depth * metersPerUnit * padding

      // Convert to degrees
      const halfLongSpan = widthMeters / metersPerDegreeLong / 2
      const halfLatSpan = depthMeters / metersPerDegreeLat / 2

      // Use max dimension for square map (better image quality)
      const maxSpan = Math.max(halfLongSpan, halfLatSpan)

      return {
        halfLongSpan: maxSpan,
        halfLatSpan: maxSpan,
        centerX: boundingExtent.centerX,
        centerZ: boundingExtent.centerZ,
        sizeWorld:
          Math.max(boundingExtent.width, boundingExtent.depth) * padding,
      }
    }

    // Fallback to fixed extent
    return {
      halfLongSpan: 0.003,
      halfLatSpan: 0.003,
      centerX: 0,
      centerZ: 0,
      sizeWorld: (0.006 * metersPerDegreeLong) / metersPerUnit,
    }
  }, [boundingExtent, metersPerUnit, metersPerDegreeLong, metersPerDegreeLat])

  const imageSize = 2048
  const apiUrl = useMemo(
    () =>
      `https://server.arcgisonline.com/ArcGIS/rest/services/${mapService}/MapServer/export?` +
      `bbox=${long - mapExtent.halfLongSpan},${lat - mapExtent.halfLatSpan},${long + mapExtent.halfLongSpan},${lat + mapExtent.halfLatSpan}` +
      `&size=${imageSize},${imageSize}` +
      `&format=png` +
      `&f=image` +
      `&bboxSR=4326&imageSR=4326`,
    [lat, long, mapService, mapExtent.halfLongSpan, mapExtent.halfLatSpan]
  )

  const mapUrl = overrideMapUrl ?? apiUrl

  const texture = useLoader(TextureLoader, mapUrl)
  const opacityRef = useRef(0)
  const [currentOpacity, setCurrentOpacity] = useState(0)

  useEffect(() => {
    if (texture && onLoad) {
      onLoad()
    }
  }, [texture, onLoad])

  // Flip texture horizontally
  texture.wrapS = RepeatWrapping
  texture.repeat.x = -1
  texture.offset.x = 1

  // Animate opacity
  useFrame((_, delta) => {
    const target = targetOpacity * baseOpacity
    const current = opacityRef.current
    const diff = target - current

    if (Math.abs(diff) > 0.01) {
      const newOpacity = current + diff * Math.min(delta * 4, 1)
      opacityRef.current = newOpacity
      setCurrentOpacity(newOpacity)
    } else if (current !== target) {
      opacityRef.current = target
      setCurrentOpacity(target)
      if (targetOpacity === 0 && onFadeComplete) {
        onFadeComplete()
      }
    }
  })

  const rotationYRad = (rotationY * Math.PI) / 180

  if (currentOpacity <= 0.01 && targetOpacity === 0) return null

  return (
    <group rotation={[0, rotationYRad, 0]}>
      <mesh
        position={[mapExtent.centerX, yPosition, mapExtent.centerZ]}
        rotation={[-Math.PI / 2, Math.PI, 0]}
        renderOrder={-1}
        receiveShadow>
        <planeGeometry args={[mapExtent.sizeWorld, mapExtent.sizeWorld]} />
        <meshBasicMaterial
          map={texture}
          side={DoubleSide}
          transparent
          opacity={currentOpacity}
          depthWrite={false}
        />
      </mesh>
    </group>
  )
}

interface MapLayerState {
  style: MapStyle
  status: 'loading' | 'active' | 'fading-out'
  /** Cached URL from rolodex, if available */
  cachedUrl?: string
}

/** Delay between retry attempts in milliseconds */
const RETRY_DELAY_MS = 2000
/** Maximum number of retry attempts */
const MAX_RETRIES = 5

/**
 * Fetches a map image from the ArcGIS API with retry logic.
 * Returns the image as a Blob on success.
 */
async function fetchMapImageWithRetry(
  lat: number,
  long: number,
  style: MapStyle,
  boundingExtent: BoundingExtent | undefined,
  metersPerUnit: number,
  retryCount = 0
): Promise<Blob> {
  const mapService =
    style === 'satellite' ? 'World_Imagery' : 'World_Street_Map'
  const imageSize = 2048

  const metersPerDegreeLat = 111320
  const metersPerDegreeLong = 111320 * Math.cos((lat * Math.PI) / 180)

  let halfLongSpan = 0.003
  let halfLatSpan = 0.003

  if (boundingExtent) {
    const padding = 1.2
    const widthMeters = boundingExtent.width * metersPerUnit * padding
    const depthMeters = boundingExtent.depth * metersPerUnit * padding
    halfLongSpan = widthMeters / metersPerDegreeLong / 2
    halfLatSpan = depthMeters / metersPerDegreeLat / 2
    const maxSpan = Math.max(halfLongSpan, halfLatSpan)
    halfLongSpan = maxSpan
    halfLatSpan = maxSpan
  }

  const mapUrl =
    `https://server.arcgisonline.com/ArcGIS/rest/services/${mapService}/MapServer/export?` +
    `bbox=${long - halfLongSpan},${lat - halfLatSpan},${long + halfLongSpan},${lat + halfLatSpan}` +
    `&size=${imageSize},${imageSize}` +
    `&format=jpg` +
    `&f=image` +
    `&bboxSR=4326&imageSR=4326`

  try {
    const response = await fetch(mapUrl)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    return await response.blob()
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      console.warn(
        `[MapGroundPlane] ${style} API failed (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS}ms...`,
        error
      )
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
      return fetchMapImageWithRetry(
        lat,
        long,
        style,
        boundingExtent,
        metersPerUnit,
        retryCount + 1
      )
    }
    throw error
  }
}

/**
 * Wrapper that handles loading state, smooth transitions between map styles,
 * caching to rolodex, and retry logic for API failures.
 */
export const MapGroundPlaneWithFallback: FC<MapGroundPlaneProps> = (props) => {
  const { kestrelModelId, lat, long, boundingExtent, metersPerUnit = 1 } = props

  const [layers, setLayers] = useState<MapLayerState[]>([
    { style: props.mapStyle || 'street', status: 'loading' },
  ])
  const prevStyleRef = useRef(props.mapStyle)
  const isFetchingRef = useRef<Set<MapStyle>>(new Set())

  const currentStyle = props.mapStyle || 'street'

  const {
    cachedImageUrl: streetCachedUrl,
    isLoading: isStreetCacheLoading,
    uploadImageToCache: uploadStreetImage,
    forceRefresh: refreshStreetCache,
  } = useMapImageCache(kestrelModelId, 'street')

  const {
    cachedImageUrl: satelliteCachedUrl,
    isLoading: isSatelliteCacheLoading,
    uploadImageToCache: uploadSatelliteImage,
    forceRefresh: refreshSatelliteCache,
  } = useMapImageCache(kestrelModelId, 'satellite')

  const getCachedUrl = useCallback(
    (style: MapStyle) =>
      style === 'satellite' ? satelliteCachedUrl : streetCachedUrl,
    [satelliteCachedUrl, streetCachedUrl]
  )

  const isCacheLoading = useCallback(
    (style: MapStyle) =>
      style === 'satellite' ? isSatelliteCacheLoading : isStreetCacheLoading,
    [isSatelliteCacheLoading, isStreetCacheLoading]
  )

  const fetchAndCacheMapImage = useCallback(
    async (style: MapStyle) => {
      if (!kestrelModelId || isFetchingRef.current.has(style)) {
        return
      }

      isFetchingRef.current.add(style)

      try {
        const imageBlob = await fetchMapImageWithRetry(
          lat,
          long,
          style,
          boundingExtent,
          metersPerUnit
        )

        const uploadFn =
          style === 'satellite' ? uploadSatelliteImage : uploadStreetImage
        await uploadFn(imageBlob, style)

        const refreshFn =
          style === 'satellite' ? refreshSatelliteCache : refreshStreetCache
        await refreshFn()
      } catch (error) {
        console.error(
          `[MapGroundPlane] Failed to fetch and cache ${style} map:`,
          error
        )
        props.onStyleFailed?.(style)
      } finally {
        isFetchingRef.current.delete(style)
      }
    },
    [
      kestrelModelId,
      lat,
      long,
      boundingExtent,
      metersPerUnit,
      uploadSatelliteImage,
      uploadStreetImage,
      refreshSatelliteCache,
      refreshStreetCache,
      props,
    ]
  )

  // Check cache and fetch if needed when style changes or cache loads
  useEffect(() => {
    if (!kestrelModelId || isCacheLoading(currentStyle)) {
      return
    }

    const cachedUrl = getCachedUrl(currentStyle)
    if (cachedUrl) {
      // Use cached image
      setLayers((prev) => {
        const existingLayer = prev.find(
          (l) => l.style === currentStyle && l.status !== 'fading-out'
        )
        if (existingLayer?.cachedUrl === cachedUrl) {
          return prev
        }

        const updated = prev.map((layer) =>
          layer.status === 'active'
            ? { ...layer, status: 'fading-out' as const }
            : layer
        )
        const filtered = updated.filter(
          (l) => l.style !== currentStyle || l.status === 'fading-out'
        )
        return [
          ...filtered,
          { style: currentStyle, status: 'loading', cachedUrl },
        ]
      })
    } else {
      // Need to fetch from API
      fetchAndCacheMapImage(currentStyle)
    }
  }, [
    kestrelModelId,
    currentStyle,
    getCachedUrl,
    isCacheLoading,
    fetchAndCacheMapImage,
  ])

  // Handle map style changes from props
  useEffect(() => {
    if (props.mapStyle && props.mapStyle !== prevStyleRef.current) {
      prevStyleRef.current = props.mapStyle

      setLayers((prev) => {
        const updated = prev.map((layer) =>
          layer.status === 'active'
            ? { ...layer, status: 'fading-out' as const }
            : layer
        )
        const cachedUrl = getCachedUrl(props.mapStyle!)
        return [
          ...updated,
          { style: props.mapStyle!, status: 'loading' as const, cachedUrl },
        ]
      })
    }
  }, [props.mapStyle, getCachedUrl])

  // Notify parent of loading state changes
  const isLoading = layers.some((l) => l.status === 'loading')
  const { onLoadingChange } = props
  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleLayerLoaded = (style: MapStyle) => {
    setLayers((prev) =>
      prev.map((layer) =>
        layer.style === style && layer.status === 'loading'
          ? { ...layer, status: 'active' }
          : layer
      )
    )
  }

  const handleFadeComplete = (style: MapStyle) => {
    setLayers((prev) =>
      prev.filter(
        (layer) => layer.style !== style || layer.status !== 'fading-out'
      )
    )
  }

  const handleLayerError = useCallback(
    (style: MapStyle, error: Error) => {
      console.warn(
        `[MapGroundPlane] Failed to load ${style} map tile:`,
        error.message
      )

      // If satellite fails, try street style
      if (style === 'satellite') {
        setLayers((prev) => {
          const filtered = prev.filter((layer) => layer.style !== 'satellite')
          const hasStreet = filtered.some((l) => l.style === 'street')
          if (!hasStreet) {
            const streetCached = getCachedUrl('street')
            return [
              ...filtered,
              { style: 'street', status: 'loading', cachedUrl: streetCached },
            ]
          }
          return filtered
        })

        if (!getCachedUrl('street')) {
          fetchAndCacheMapImage('street')
        }
        props.onStyleFailed?.(style)
        return
      }

      // If street style also fails, retry
      setLayers((prev) => prev.filter((layer) => layer.style !== style))
      fetchAndCacheMapImage(style)
    },
    [getCachedUrl, fetchAndCacheMapImage, props]
  )

  // Filter to only render layers with cached URLs
  const renderableLayers = layers.filter((layer) => layer.cachedUrl)

  return (
    <>
      {/* Loading placeholder - shown while loading or as fallback when all layers fail */}
      {(isLoading || renderableLayers.length === 0) && (
        <MapGroundPlanePlaceholder {...props} />
      )}

      {/* Map layers with suspense and error boundary */}
      {renderableLayers.map((layer) => (
        <ErrorBoundary
          key={`${layer.style}-${layer.status}-${layer.cachedUrl}`}
          fallback={null}
          onError={(error) => handleLayerError(layer.style, error)}>
          <React.Suspense fallback={null}>
            <MapLayer
              {...props}
              mapStyle={layer.style}
              targetOpacity={layer.status === 'fading-out' ? 0 : 1}
              onLoad={() => handleLayerLoaded(layer.style)}
              onFadeComplete={() => handleFadeComplete(layer.style)}
              overrideMapUrl={layer.cachedUrl}
            />
          </React.Suspense>
        </ErrorBoundary>
      ))}
    </>
  )
}

/**
 * Grid placeholder while map loads, sized to bounding extent
 */
const MapGroundPlanePlaceholder: FC<MapGroundPlaneProps> = ({
  yPosition = -30,
  boundingExtent,
}) => {
  const size = boundingExtent
    ? Math.max(boundingExtent.width, boundingExtent.depth) * 1.5
    : 1000
  const centerX = boundingExtent?.centerX ?? 0
  const centerZ = boundingExtent?.centerZ ?? 0

  return (
    <gridHelper
      args={[size, Math.min(100, Math.floor(size / 10)), '#3a4a3a', '#2a3a2a']}
      position={[centerX, yPosition, centerZ]}
      renderOrder={-2}
    />
  )
}

/** @deprecated Use MapGroundPlaneWithFallback instead */
export const MapGroundPlane = MapLayer
